import type {ColorThemePresetId} from '@core/constants/colorThemePresets';

/** One Monaco theme id per color preset and mode (see PRESET_MONACO_THEME_DEFINITIONS). */
export type PresetMonacoThemeId =
  | `preset-${ColorThemePresetId}`
  | `preset-${ColorThemePresetId}-dark`;

/** Saved editor themes (style.json): not tied to a color-palette preset id. */
export type CustomMonacoEditorThemeId = 'custom-light' | 'custom-dark';

export type MonacoEditorThemeOverrideKey =
  | PresetMonacoThemeId
  | CustomMonacoEditorThemeId;

export type MonacoEditorThemeDefinitionId =
  | PresetMonacoThemeId
  | CustomMonacoEditorThemeId;

export interface PresetMonacoRule {
  token?: string;
  foreground?: string;
  fontStyle?: string;
}

/** Shape compatible with built-in preset definitions in monacoPresetThemeDefinitions.ts */
export interface PresetMonacoDefinition {
  id: MonacoEditorThemeDefinitionId;
  base: 'vs' | 'vs-dark';
  backgroundColor: string;
  foregroundColor: string;
  colors?: Record<string, string>;
  rules: PresetMonacoRule[];
}

/** Monaco rule foreground values omit `#`. */
function fg(hex: string): string {
  return hex.replace(/^#/, '');
}

/**
 * Optional colours for {@link UnifiedDiffView} when this preset Monaco theme is active.
 * Stored under `monacoPresetThemeOverrides`.
 */
export interface UnifiedDiffViewColorOverrides {
  deletionBg?: string;
  deletionFg?: string;
  additionBg?: string;
  additionFg?: string;
}

/**
 * Partial overrides for one preset Monaco theme (merged onto built-in defaults).
 * Stored in style.json under `monacoPresetThemeOverrides`.
 */
export interface MonacoPresetThemeOverride {
  backgroundColor?: string;
  foregroundColor?: string;
  colors?: Record<string, string>;
  /** Keyed by token (e.g. `string`, `keyword.json`). */
  ruleOverrides?: Record<string, {foreground?: string; fontStyle?: string}>;
  /** Unified diff line colours (insertion / deletion). */
  unifiedDiffView?: UnifiedDiffViewColorOverrides;
}

export type MonacoPresetThemeOverrides = Partial<
  Record<MonacoEditorThemeOverrideKey, MonacoPresetThemeOverride>
>;

/**
 * Returns a shallow copy of overrides. Nested objects (`colors`, `ruleOverrides`, etc.) are
 * shared with `input`; mutating the returned value’s nested fields can mutate stored state.
 */
export function mergeMonacoPresetThemeOverrides(
  input?: MonacoPresetThemeOverrides | null,
): MonacoPresetThemeOverrides {
  if (!input || typeof input !== 'object') {
    return {};
  }
  return {...input};
}

/** True if style.json should persist a non-empty override object for this theme. */
export function monacoPresetThemeOverrideHasContent(
  o?: MonacoPresetThemeOverride | null,
): boolean {
  if (!o || typeof o !== 'object') {
    return false;
  }
  if (o.backgroundColor || o.foregroundColor) {
    return true;
  }
  if (o.colors && Object.keys(o.colors).length > 0) {
    return true;
  }
  if (o.ruleOverrides && Object.keys(o.ruleOverrides).length > 0) {
    return true;
  }
  if (o.unifiedDiffView && Object.keys(o.unifiedDiffView).length > 0) {
    return true;
  }
  return false;
}

function mergeRule(
  base: PresetMonacoRule,
  ov?: {foreground?: string; fontStyle?: string},
): PresetMonacoRule {
  if (!ov) {
    return base;
  }
  const next = {...base};
  if (ov.foreground !== undefined && ov.foreground !== '') {
    next.foreground = fg(ov.foreground);
  }
  if (ov.fontStyle !== undefined) {
    next.fontStyle = ov.fontStyle;
  }
  return next;
}

/**
 * Merge built-in preset definition with saved overrides (for Monaco + meta).
 * `ruleOverrides` only updates tokens that already exist in `def.rules`; unknown
 * token keys are ignored (we do not invent new rules).
 *
 * Duplicate `token` values in `def.rules` collapse to a single map entry (last wins).
 */
export function mergePresetMonacoDefinition(
  def: PresetMonacoDefinition,
  override?: MonacoPresetThemeOverride | null,
): PresetMonacoDefinition {
  if (!override) {
    return def;
  }

  const byToken = new Map(def.rules.map(r => [String(r.token ?? ''), {...r}]));

  for (const [token, ov] of Object.entries(override.ruleOverrides ?? {})) {
    const existing = byToken.get(token);
    if (existing) {
      byToken.set(token, mergeRule(existing, ov));
    }
  }

  return {
    ...def,
    backgroundColor: override.backgroundColor ?? def.backgroundColor,
    foregroundColor: override.foregroundColor ?? def.foregroundColor,
    colors: {...def.colors, ...override.colors},
    rules: Array.from(byToken.values()),
  };
}

function normRuleFg(f?: string): string {
  if (!f) {
    return '';
  }
  return f.replace(/^#/, '').toLowerCase();
}

function diffColorRecordOverrides(
  baseColors: Record<string, string>,
  targetColors: Record<string, string>,
): Record<string, string> | undefined {
  const colors: Record<string, string> = {};
  const keys = new Set([
    ...Object.keys(baseColors),
    ...Object.keys(targetColors),
  ]);
  for (const key of keys) {
    if (
      targetColors[key] === baseColors[key] ||
      targetColors[key] === undefined
    ) {
      continue;
    }
    colors[key] = targetColors[key];
  }
  return Object.keys(colors).length > 0 ? colors : undefined;
}

function targetRuleDiffersFromBase(
  br: PresetMonacoRule | undefined,
  tr: PresetMonacoRule,
): boolean {
  if (!br) {
    return true;
  }
  return (
    normRuleFg(tr.foreground) !== normRuleFg(br.foreground) ||
    tr.fontStyle !== br.fontStyle
  );
}

function ruleToOverrideEntry(
  br: PresetMonacoRule | undefined,
  tr: PresetMonacoRule,
): {foreground?: string; fontStyle?: string} {
  const fore = tr.foreground ? `#${fg(tr.foreground)}` : undefined;
  return {
    foreground: fore,
    fontStyle: tr.fontStyle === br?.fontStyle ? undefined : tr.fontStyle,
  };
}

function diffRuleOverridesFromRules(
  baseRules: PresetMonacoRule[],
  targetRules: PresetMonacoRule[],
): Record<string, {foreground?: string; fontStyle?: string}> | undefined {
  const baseByToken = new Map(baseRules.map(r => [String(r.token ?? ''), r]));
  const ruleOverrides: Record<
    string,
    {foreground?: string; fontStyle?: string}
  > = {};
  for (const tr of targetRules) {
    const token = String(tr.token ?? '');
    const br = baseByToken.get(token);
    if (!targetRuleDiffersFromBase(br, tr)) {
      continue;
    }
    ruleOverrides[token] = ruleToOverrideEntry(br, tr);
  }
  return Object.keys(ruleOverrides).length > 0 ? ruleOverrides : undefined;
}

/**
 * Expresses `target` as a partial override on top of `base` (same shape as style.json overrides).
 * `foreground: undefined` in the result may be omitted vs present depending on JSON serialization;
 * consumers should treat both consistently.
 */
export function diffPresetDefinitionToOverride(
  base: PresetMonacoDefinition,
  target: PresetMonacoDefinition,
): MonacoPresetThemeOverride {
  const o: MonacoPresetThemeOverride = {};
  if (target.backgroundColor !== base.backgroundColor) {
    o.backgroundColor = target.backgroundColor;
  }
  if (target.foregroundColor !== base.foregroundColor) {
    o.foregroundColor = target.foregroundColor;
  }
  const colors = diffColorRecordOverrides(
    base.colors ?? {},
    target.colors ?? {},
  );
  if (colors) {
    o.colors = colors;
  }
  const ruleOverrides = diffRuleOverridesFromRules(base.rules, target.rules);
  if (ruleOverrides) {
    o.ruleOverrides = ruleOverrides;
  }
  return o;
}

/**
 * Keys are taken from `Object.keys` on both maps and cast to theme ids; invalid keys in persisted
 * JSON would still be merged (trusted persistence assumed).
 */
export function mergePresetOverrideLayers(
  base: MonacoPresetThemeOverrides | undefined,
  high: MonacoPresetThemeOverrides | undefined,
): MonacoPresetThemeOverrides {
  const ids = new Set([...Object.keys(base ?? {}), ...Object.keys(high ?? {})]);
  const out: MonacoPresetThemeOverrides = {};
  for (const id of ids) {
    const key = id as MonacoEditorThemeOverrideKey;
    const b = base?.[key];
    const h = high?.[key];
    if (!h && !b) {
      continue;
    }
    if (!h) {
      out[key] = b!;
      continue;
    }
    if (!b) {
      out[key] = {
        ...h,
        colors: {...h.colors},
        ruleOverrides: {...h.ruleOverrides},
      };
      continue;
    }
    out[key] = {
      ...b,
      ...h,
      colors: {...b.colors, ...h.colors},
      ruleOverrides: {...b.ruleOverrides, ...h.ruleOverrides},
      unifiedDiffView: {
        ...b.unifiedDiffView,
        ...h.unifiedDiffView,
      },
    };
  }
  return out;
}
